import React from "react";
import { useTranslate } from "@refinedev/core";
import { Col, Empty, Flex, Row } from "antd";
import type { AddonDetail, AddonItemStat } from "types/";
import { AnalyticsLineChart } from "../AnalyticsLineChart";
import { formatPct } from "../utils/formatPct";
import { AnalyticsCard } from "../AnalyticsCard";
import { analyticsTheme } from "../analyticsTheme";
import { chartPlotHeight } from "./utils/chartSizing";
import { HeatTable, type HeatCell, type HeatRow } from "../HeatTable";
import { ItemRows, type ItemRow } from "../ItemRows";
import {
  ADDON_COLORS,
  HARD_QUESTION_RATE,
  LOW_SAMPLE,
  QUESTION_TYPES,
  buildQuizMatrix,
  isHardQuestion,
  isLowSample,
  itemRate,
  reachCurve,
  successShade,
  SUCCESS_BANDS,
  type MatrixCell,
} from "./utils/metrics";

const T_PREFIX = "media.projects.analytics.addons";
const GOOD = analyticsTheme.metricSuccess;
const BAD = analyticsTheme.metricError;

export type QuizResultsProps = {
  detail: AddonDetail;
  /** Answers merged per question position, whatever the publication or wording. */
  positions: AddonItemStat[];
  /** One entry per question of the pool, in order. */
  questions: AddonItemStat[];
  sessionsInteracted: number;
};

const binColor = (minScore: number) => {
  if (minScore >= 70) return GOOD;
  if (minScore >= 50) return analyticsTheme.metricOrange;
  return BAD;
};

export const QuizResults: React.FC<QuizResultsProps> = ({
  detail,
  positions,
  questions,
  sessionsInteracted,
}) => {
  const t = useTranslate();
  const number = (index: number) =>
    t(`${T_PREFIX}.itemNumber.quiz`, { n: index + 1 });
  // Types the frontend doesn't know yet show as the backend sends them.
  const typeLabel = (type: string) =>
    QUESTION_TYPES.includes(type) ? t(`${T_PREFIX}.quiz.types.${type}`) : type;
  const hasItems = detail.items.length > 0;

  const questionRows: ItemRow[] = questions.map((item) => {
    const rate = itemRate(item);
    const hard = isHardQuestion(rate);
    const n = number(item.item_index);
    return {
      key: String(item.item_index),
      label: n,
      text: item.label ?? t(`${T_PREFIX}.itemFallback`, { n }),
      hard,
      muted: item.touches === 0,
      tags: [
        ...(item.question_type ? [{ text: typeLabel(item.question_type) }] : []),
        ...(item.topic ? [{ text: item.topic }] : []),
        ...(hard ? [{ text: t(`${T_PREFIX}.quiz.hard`), danger: true }] : []),
      ],
      value:
        item.touches === 0 ? (
          t(`${T_PREFIX}.quiz.noAnswer`)
        ) : (
          <>
            <strong>{formatPct(rate)}</strong> · {item.touches.toLocaleString()}
          </>
        ),
      tooltip:
        item.touches === 0
          ? t(`${T_PREFIX}.quiz.noAnswer`)
          : [
              t(`${T_PREFIX}.quiz.correctOf`, {
                correct: item.correct ?? 0,
                touches: item.touches,
              }),
              ...(hard
                ? [
                    t(`${T_PREFIX}.quiz.hardReason`, {
                      min: HARD_QUESTION_RATE,
                    }),
                  ]
                : []),
            ].join(" · "),
      bar:
        rate == null ? null : { percent: rate, color: GOOD, trackColor: BAD },
    };
  });

  // Staging and prod serve the detail without it until the backend ships.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const bins = detail.score_distribution ?? [];
  const binMax = Math.max(...bins.map((b) => b.viewers), 1);
  const scoreRows: ItemRow[] = bins.map((bin) => ({
    key: `bin:${bin.min_score}`,
    text: `${bin.min_score}–${bin.min_score === 90 ? 100 : bin.min_score + 9}%`,
    value: <strong>{bin.viewers.toLocaleString()}</strong>,
    tooltip: t(`${T_PREFIX}.quiz.viewersCount`, { count: bin.viewers }),
    bar: {
      percent: (bin.viewers / binMax) * 100,
      color: binColor(bin.min_score),
    },
  }));
  const scoresCard =
    scoreRows.length > 0 ? (
      <AnalyticsCard
        title={t(`${T_PREFIX}.quiz.scoresTitle`)}
        subtitle={t(`${T_PREFIX}.quiz.scoresSubtitle`)}
      >
        <ItemRows rows={scoreRows} />
      </AnalyticsCard>
    ) : null;

  const matrix = buildQuizMatrix(positions);
  const toCell = (cell: MatrixCell | null, margin = false): HeatCell | null =>
    cell
      ? {
          ...successShade(cell.rate),
          text: formatPct(cell.rate),
          sub: t(`${T_PREFIX}.quiz.questionsShort`, { count: cell.questions }),
          faded: isLowSample(cell.touches),
          tooltip: t(`${T_PREFIX}.quiz.matrixTooltip`, {
            pct: formatPct(cell.rate),
            questions: cell.questions,
            answers: cell.touches,
          }),
          margin,
        }
      : null;
  const all = t(`${T_PREFIX}.quiz.all`);
  const legend = [
    ...SUCCESS_BANDS.map(({ background, label }) => ({
      color: background,
      label,
    })),
    {
      color: analyticsTheme.heatEmpty,
      label: t(`${T_PREFIX}.quiz.legendNone`),
    },
  ];
  const matrixRows: HeatRow[] = matrix
    ? [
        ...matrix.topics.map((topic) => ({
          key: topic,
          header: <strong>{topic}</strong>,
          title: topic,
          cells: [
            ...matrix.types.map((type) => toCell(matrix.cell(topic, type))),
            toCell(matrix.cell(topic, null), true),
          ],
        })),
        {
          key: "__all",
          header: <strong>{all}</strong>,
          cells: [
            ...matrix.types.map((type) =>
              toCell(matrix.cell(null, type), true)
            ),
            toCell(matrix.cell(null, null), true),
          ],
        },
      ]
    : [];

  if (!hasItems)
    // Answers exist but cannot be matched to questions yet. The score
    // distribution stays visible.
    return (
      <Flex vertical gap={16}>
        <Empty
          description={t(`${T_PREFIX}.empty.noQuestions`, {
            count: sessionsInteracted,
          })}
        />
        {scoresCard}
      </Flex>
    );

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <AnalyticsCard
          title={t(`${T_PREFIX}.quiz.questionsTitle`)}
          subtitle={t(`${T_PREFIX}.quiz.questionsSubtitle`)}
        >
          <ItemRows rows={questionRows} />
        </AnalyticsCard>
      </Col>
      <Col xs={24} lg={12}>
        <AnalyticsLineChart
          title={t(`${T_PREFIX}.quiz.reachTitle`)}
          subtitle={t(`${T_PREFIX}.quiz.reachSubtitle`)}
          labels={questions.map((i) => number(i.item_index))}
          datasets={[
            {
              label: t(`${T_PREFIX}.quiz.reachSeries`),
              data: reachCurve(questions, sessionsInteracted),
              color: ADDON_COLORS.quiz,
            },
          ]}
          plotHeight={chartPlotHeight(200)}
          chartOptions={{ plugins: { legend: { display: false } } }}
        />
      </Col>
      <Col xs={24} lg={12}>
        {scoresCard}
      </Col>
      <Col span={24}>
        <AnalyticsCard
          title={t(`${T_PREFIX}.quiz.matrixTitle`)}
          subtitle={t(`${T_PREFIX}.quiz.matrixSubtitle`)}
          info={t(`${T_PREFIX}.quiz.matrixInfo`, { min: LOW_SAMPLE })}
        >
          {matrix ? (
            <HeatTable
              columns={[...matrix.types.map(typeLabel), all]}
              rows={matrixRows}
              legend={legend}
            />
          ) : (
            // The quiz predates question types and topics.
            <Empty description={t(`${T_PREFIX}.empty.noTypes`)} />
          )}
        </AnalyticsCard>
      </Col>
    </Row>
  );
};
